import { useCallback } from 'react'

import { useQuery } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { buildPaginatedEndpoint, convertDatesInObject } from '@/helpers/api'
import { SweetspotError } from '@/helpers/error'

import { normalizeOrgIds, pursuitsQueryKeys } from '@/hooks/pursuits/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { PaginateRequest, PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'
import type { Pursuit } from '@/types/pursuit/Pursuit'

const PAGE_SIZE = 10000
const STALE_TIME_MS = 1000 * 60 * 5

interface CommonProps<TResult> {
  /** Applied to the merged, flat Pursuit[]. Memoized by React Query. */
  selector?: (pursuits: Pursuit[]) => TResult
}

interface SingleOrgProps<TResult> extends CommonProps<TResult> {
  orgId: UUID | ''
  orgIds?: never
}

interface MultiOrgProps<TResult> extends CommonProps<TResult> {
  orgIds: UUID[]
  orgId?: never
}

type UsePursuitsProps<TResult> =
  | SingleOrgProps<TResult>
  | MultiOrgProps<TResult>

interface UsePursuitsResult<TResult> {
  data: TResult | undefined
  isLoading: boolean
  isFetching: boolean
  isError: boolean
  errors: Error[]
  /** orgIds whose fetch errored. */
  failedOrgIds: UUID[]
  refetch: () => Promise<unknown>
}

export const usePursuits = <TResult = Pursuit[]>(
  args: UsePursuitsProps<TResult>,
): UsePursuitsResult<TResult> => {
  const { accessToken } = useAuthInfoWithThrow()
  const { selector } = args
  const orgIds = normalizeOrgIds(args)
  const isSingle = orgIds.length <= 1
  const selectPursuits = useCallback(
    (response: PaginatedResponse<Pursuit>) => {
      const pursuits = response.data
      return selector ? selector(pursuits) : (pursuits as unknown as TResult)
    },
    [selector],
  )
  const placeholderPursuits = useCallback(
    (previousData: PaginatedResponse<Pursuit> | undefined) => {
      if (!previousData) return previousData
      const orgIdSet = new Set(orgIds)
      const data = previousData.data.filter((pursuit) =>
        orgIdSet.has(pursuit.organization_id),
      )
      return {
        ...previousData,
        count: data.length,
        has_more: false,
        data,
      }
    },
    [orgIds],
  )

  // Single-org fast path keeps a stable useQuery identity across orgIds reference changes.
  const singleOrgId = orgIds[0] ?? ''
  const singleQuery = useQuery({
    queryKey: pursuitsQueryKeys.organization({ orgId: singleOrgId }),
    queryFn: () =>
      fetchPursuits(singleOrgId, accessToken, { limit: PAGE_SIZE }),
    staleTime: STALE_TIME_MS,
    enabled: isSingle && !!singleOrgId && !!accessToken,
    select: selectPursuits,
  })

  const multiQuery = useQuery({
    queryKey: pursuitsQueryKeys.organizationsOverview({ orgIds }),
    queryFn: () =>
      fetchOrganizationsOverviewPursuits(orgIds, accessToken, {
        limit: PAGE_SIZE,
      }),
    staleTime: STALE_TIME_MS,
    enabled: !isSingle && orgIds.length > 0 && !!accessToken,
    placeholderData: placeholderPursuits,
    select: selectPursuits,
  })

  const data = isSingle ? singleQuery.data : multiQuery.data

  if (isSingle) {
    return {
      data,
      isLoading: singleQuery.isLoading,
      isFetching: singleQuery.isFetching,
      isError: singleQuery.isError,
      errors: singleQuery.error ? [singleQuery.error as Error] : [],
      failedOrgIds: singleQuery.isError && singleOrgId ? [singleOrgId] : [],
      refetch: () => singleQuery.refetch(),
    }
  }

  return {
    data,
    isLoading: multiQuery.isLoading,
    isFetching: multiQuery.isFetching,
    isError: multiQuery.isError,
    errors: multiQuery.error ? [multiQuery.error as Error] : [],
    failedOrgIds: multiQuery.isError ? orgIds : [],
    refetch: () => multiQuery.refetch(),
  }
}

export const fetchOrganizationsOverviewPursuits = async (
  orgIds: UUID[],
  accessToken: string | undefined | null,
  pagination: PaginateRequest,
): Promise<PaginatedResponse<Pursuit>> => {
  if (
    accessToken === null ||
    accessToken === undefined ||
    orgIds.length === 0
  ) {
    return { count: 0, has_more: false, data: [] }
  }

  const params = new URLSearchParams()
  for (const orgId of orgIds) {
    params.append('organization_ids', orgId)
  }
  params.set('limit', pagination.limit.toString())
  if (pagination.offset !== undefined) {
    params.set('offset', pagination.offset.toString())
  }

  const res: Response = await fetch(
    `${API_URL}/api/v1/organizations/pursuits/overview?${params.toString()}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    },
  )
  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: 'Failed to fetch overview pursuits',
      cause: res,
    })
  }
  return convertDatesInObject(await res.json()) as PaginatedResponse<Pursuit>
}

export const fetchPursuits = async (
  orgId: UUID | '',
  accessToken: string | undefined | null,
  pagination: PaginateRequest,
): Promise<PaginatedResponse<Pursuit>> => {
  if (accessToken === null || accessToken === undefined || !orgId) {
    return { count: 0, has_more: false, data: [] }
  }
  const url = buildPaginatedEndpoint(
    `${API_URL}/api/v1/organizations/${orgId}/pursuits`,
    pagination,
  )
  const res: Response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: 'Failed to fetch pursuits',
      cause: res,
    })
  }
  return convertDatesInObject(await res.json()) as PaginatedResponse<Pursuit>
}
